import { Button, Typography } from '@mui/material';
import { graphql, useStaticQuery } from 'gatsby';
import * as React from "react"
import { useDispatch, useSelector } from "react-redux";

import { setOpen as setChatOpen } from 'components/Chat/slice';
import Fluid from 'components/Fluid';
import Link from 'components/Link';
import useLiveData from 'hooks/useLiveData';
import { ReactComponent as Close } from 'images/icons/close.svg';
import { ReactComponent as Crab } from 'images/icons/crab.svg';
import { ReactComponent as Wave } from 'images/icons/wave.svg';
import { ReactComponent as LogoBlue } from 'images/logo_blue.svg';
import { ReactComponent as LogoSmallBlue } from 'images/logo_small_blue.svg';
import { ReactComponent as LogoSmall } from 'images/logo_small.svg';
import { ReactComponent as Logo } from 'images/logo.svg';
import { ReactComponent as LongArrow } from 'images/long_arrow.svg';
import { setLocked } from 'utils/bodySlice';
import { useGetWeatherQuery } from "utils/conditionsSlice";
import { WEATHER_ICONS } from 'utils/constants';
import debounce from 'utils/debounce';
import { useGetFlagQuery } from "utils/flagSlice";

import { setOpen as setNavOpen, setOpaque, setSize, setTransparent } from './slice';

const Navigation = (props) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [dropDownMenu, setDropDownMenu] = React.useState(null);
	const { pageContext } = props;
	const dispatch = useDispatch();
	const variant = useSelector((state) => state.nav.variant);
	const size = useSelector((state) => state.nav.size);
	const { data: conditions } = useGetWeatherQuery();
	const { data: flagConditions } = useGetFlagQuery();
	const temperature = conditions?.temperature;
	const forecast = conditions?.forecast;
	const icon = conditions?.icon;
	const data = useStaticQuery(graphql`
		query {
		  allContentMenu (filter: {menuID: {in: ["main_experiences", "main_plan_your_trip"]}}) {
		    edges {
		      node {
				menuID
		        pages
		      }
		    }
		  }

		  allBeachNeighborhood(sort: {sort: ASC}) {
		    edges {
		      node {
		        name
		        permalink
		      }
		    }
		  }
		}
	`);
	const { response: menuResponse } = useLiveData({
		type: 'ContentMenu',
		method: 'all',
		apiParams: {
			menuID: 'main_experiences,main_plan_your_trip',
		},
		staticData: data,
	});
	const { response: beachResponse } = useLiveData({
		type: 'BeachNeighborhood',
		method: 'all',
		apiParams: {
			_sort: 'sort',
		},
		staticData: data,
	});

	const handleMenu = (evt) => {
		setIsOpen(!isOpen);
		setDropDownMenu(null);
	}

	const handleChat = (evt) => {
		dispatch(setChatOpen(true));
		setDropDownMenu(null);
	}

	const handleScroll = React.useMemo(() => debounce((evt) => {
		const masthead = document.querySelector('.masthead:not(.masthead--no-image');
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
		let offset = 200;
		if (masthead) {
			const height = masthead.getBoundingClientRect().height;
			offset = Math.max(offset, height-200);
		}
		if (scrollTop >= offset)dispatch(setSize('small'));
		else dispatch(setSize());
	}), [dispatch]);

	const WeatherIcon = WEATHER_ICONS[icon];

	/**
	 * @TODO: Need to check and make that the functions of the useEffect hooks are memoized so that they don't needlessly cause re-rendering; check that this component renders efficiently
	 *
	 */
	React.useEffect(() => {
		dispatch(setNavOpen(isOpen));
		if (isOpen) {
			dispatch(setOpaque());
			dispatch(setLocked(true));
		} else {
			dispatch(setTransparent());
			dispatch(setLocked(false));
		}
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		}
	}, [isOpen, dispatch, handleScroll]);

	const experiences = menuResponse?.data?.find(item => item.menuID === 'main_experiences')?.pages || [];
	const planYourTrip = menuResponse?.data?.find(item => item.menuID === 'main_plan_your_trip')?.pages || [];
	return (
		<nav className={`mainNav mainNav--${(pageContext.navVariant && variant !== 'opaque' ? pageContext.navVariant : variant)} ${pageContext.navDefault ? `mainNav--initial--${pageContext.navDefault}` : ''} ${size ? `mainNav--${size}` : ''} ${(isOpen ? 'mainNav--isOpen' : '')}`}>
			<section className="mainNav__mainContainer">
				<Fluid layout="1/3" collapsepoint="xs" full justifyContent="space-between">
					<Button className="mainNav__menuButton" onClick={handleMenu} disableRipple>
						<div className="mainNav__menuButton__iconContainer">
							{ isOpen && <Close /> }
							{ !isOpen && <Wave /> }
						</div>
						{ isOpen && <span className="mainNav__button__label">Close</span> }
						{ !isOpen && <span className="mainNav__button__label">Menu</span> }
					</Button>
					<div className="mainNav__logoContainer">
						<Link href="/" onClick={() => {setIsOpen(false)}}>
							{variant === 'opaque' && <LogoBlue className="mainNav__logoContainer__logo-large" alt="South Walton County Florida" />}
							{variant === 'opaque' && <LogoSmallBlue className="mainNav__logoContainer__logo-small" alt="South Walton County Florida" />}
							{variant !== 'opaque' && <Logo className="mainNav__logoContainer__logo-large" alt="South Walton County Florida" />}
							{variant !== 'opaque' && <LogoSmall className="mainNav__logoContainer__logo-small" alt="South Walton County Florida" />}
						</Link>
					</div>
					<Button className="mainNav__chatButton" onClick={() => {handleChat(); setIsOpen(false);}} disableRipple>
						<span className="mainNav__button__label">Questions</span>
						<Crab />
					</Button>
				</Fluid>
			</section>
			<section className={`mainNav__dropdown ${(isOpen ? 'mainNav__dropdown--isOpen' : '')}`}>
				<div className="mainNav__gridContainer">
					<div className={`mainNav__dropdown__sectionContainer mainNav__dropdown__discover ${dropDownMenu !== 'discover' && dropDownMenu !== null ? 'mainNav__dropdown__sectionContainer-notOpen' : ''}${dropDownMenu === 'discover' ? 'mainNav__dropdown__sectionContainer-open' : ''}`}>
						<div className="mainNav__dropdown__sectionHeader">
							<Typography variant="h4" component="div" className="mainNav__dropdown__sectionHeader__text"><Link href="/neighborhoods/" onClick={handleMenu}>Discover South Walton<LongArrow className="mainNav__dropdown__sectionHeader__text__longArrow" /></Link></Typography>
							<Button className="mainNav__dropdown__sectionHeader__button" disableRipple onClick={() => setDropDownMenu((dropDownMenu === 'discover' ? null : 'discover'))} aria-label="open menu"><LongArrow /></Button>
						</div>
						<div className="mainNav__dropdown__sectionContent">
							<ul>
								{ beachResponse.data.map((item) => <li key={item.permalink}><Link onClick={handleMenu} href={`/neighborhoods/${item.permalink}/`}>{item.name}</Link></li>)}
							</ul>
						</div>
					</div>
					<div className={`mainNav__dropdown__sectionContainer mainNav__dropdown__experiences ${dropDownMenu !== 'experiences' && dropDownMenu !== null ? 'mainNav__dropdown__sectionContainer-notOpen' : ''} ${dropDownMenu === 'experiences' ? 'mainNav__dropdown__sectionContainer-open' : ''}`}>
						<div className="mainNav__dropdown__sectionHeader">
							<Typography variant="h4" component="div" className="mainNav__dropdown__sectionHeader__text"><Link href="/things-to-do/" onClick={handleMenu}>Experiences<LongArrow className="mainNav__dropdown__sectionHeader__text__longArrow" /></Link></Typography>
							<Button className="mainNav__dropdown__sectionHeader__button" disableRipple onClick={() => setDropDownMenu((dropDownMenu === 'experiences' ? null : 'experiences'))} aria-label="open menu"><LongArrow /></Button>
						</div>
						<div className="mainNav__dropdown__sectionContent">
							<ul>
								{ experiences.map((item) => (item.visible === 'true' || item.visible === true) && <li key={item.id}><Link href={item.href} onClick={handleMenu} target={item.target}>{item.text}</Link></li>)}
							</ul>
						</div>
					</div>
					<div className={`mainNav__dropdown__sectionContainer mainNav__dropdown__planYourTrip ${dropDownMenu !== 'planYourTrip' && dropDownMenu !== null ? 'mainNav__dropdown__sectionContainer-notOpen' : ''} ${dropDownMenu === 'planYourTrip' ? 'mainNav__dropdown__sectionContainer-open' : ''}`}>
						<div className="mainNav__dropdown__sectionHeader">
							<Typography variant="h4" component="div" className="mainNav__dropdown__sectionHeader__text"><Link href="/plan-your-trip/" onClick={handleMenu}>Plan Your Trip<LongArrow className="mainNav__dropdown__sectionHeader__text__longArrow" /></Link></Typography>
							<Button className="mainNav__dropdown__sectionHeader__button" disableRipple onClick={() => setDropDownMenu((dropDownMenu === 'planYourTrip' ? null : 'planYourTrip'))} aria-label="open menu"><LongArrow /></Button>
						</div>
						<div className="mainNav__dropdown__sectionContent">
							<ul>
								{ planYourTrip.map((item) => (item.visible === 'true' || item.visible === true) && <li key={item.id}><Link onClick={handleMenu} href={item.href} target={item.target}>{item.text}</Link></li>)}
							</ul>
						</div>
					</div>
					<div className="mainNav__gridContainer2">
						<div className="mainNav__dropdown__beachBayAccess">
							<Typography variant="h4" component="div" className="mainNav__dropdown__sectionHeader__text">
								<Link onClick={handleMenu} href="/meetings/">Meetings<LongArrow className="mainNav__dropdown__sectionHeader__text__longArrow" /></Link>
							</Typography>
							<Typography variant="h4" component="div" className="mainNav__dropdown__sectionHeader__text">
								<Link onClick={handleMenu} href="/weddings/">Weddings<LongArrow className="mainNav__dropdown__sectionHeader__text__longArrow" /></Link>
							</Typography>
						</div>
						<div className="mainNav__dropdown__conditions">
							<Typography variant="h4" component="div">
								<div className="mainNav__dropdown__conditions__line">
									<div className="mainNav__dropdown__conditions__line__weather">
										{ temperature && <>{temperature}&deg;</> }
										{ WeatherIcon && <WeatherIcon className={icon} aria-label={forecast} /> }
									</div>
									{ flagConditions &&
										<div className="mainNav__dropdown__conditions__line__beach">
											<div className="mainNav__dropdown__conditions__line__beach__conditions">
												<Typography variant="h4" component="div" className="mainNav__dropdown__conditions__line__beach__conditions__primary">{flagConditions.condition}</Typography>
												{ flagConditions.secondaryCondition && <Typography variant="h6" component="div" className="mainNav__dropdown__conditions__line__beach__conditions__secondary">{flagConditions.secondaryCondition}</Typography> }
											</div>
											<div className="mainNav__dropdown__conditions__line__beach__flags">
												<img src={flagConditions.flag} alt={flagConditions.condition} />
												{ flagConditions.secondaryFlag && <img src={flagConditions.secondaryFlag} alt={flagConditions.secondaryCondition} /> }
											</div>
										</div>
									}
								</div>
							</Typography>
						</div>
					</div>
				</div>
			</section>
		</nav>
	)
}

export default React.memo(Navigation);